import React, { useMemo, useState } from 'react';
import { useSlateStatic } from 'slate-react';
import { Button, Drawer } from '@ublend-npm/aulaui-next';
import { Path } from 'slate';
import {
  AulaEditorContext,
  AulaEditorInstance,
  ContentTemplate,
} from '../../types';
import { TemplatesDrawerProps } from './types';
import { TemplateChoiceOption } from './TemplateChoiceOption';
import { TemplatePreview } from './TemplatePreview';
import {
  ActionButtonsContainer,
  CategoryTitle,
  TemplatesDrawerContainer,
  TemplatesListContainer,
  TemplatesListContainerInner,
  TemplatesTitle,
} from './TemplatesDrawer.styled';
import { insertBlock } from '../../lib/commands';
import { useAnalytics } from '../../lib/useAnalytics';

const insertTemplate = (
  editor: AulaEditorInstance,
  context: AulaEditorContext,
  path: Path,
  selectedTemplateId: string,
  templates: ContentTemplate[],
) => {
  const selectedTemplate = templates.find(
    (template: ContentTemplate) => template.id === selectedTemplateId,
  );

  if (!selectedTemplate) return;

  const {
    content: {
      context: { entities },
      value,
    },
  } = selectedTemplate;

  // Add entities from template to the editor entities
  const entityKeys = Object.keys(entities);
  entityKeys.forEach((key) => context.updateEntity(key, entities[key]));

  insertBlock(editor, path, value);
};

export const TemplatesDrawer = ({
  context,
  templates,
}: TemplatesDrawerProps) => {
  const { templatesPath, setTemplatesPath } = context;

  const editor = useSlateStatic();
  const track = useAnalytics();

  const categorizedTemplates = useMemo(() => {
    const templatesPerCategory = templates.reduce(
      (final, template) => {
        if (final[template.category]) {
          final[template.category].push(template);
        } else {
          final[template.category] = [template];
        }
        return final;
      },
      {} as Record<string, Array<ContentTemplate>>,
    );

    return Object.keys(templatesPerCategory)
      .sort()
      .map((category) => ({
        category,
        elements: templatesPerCategory[category],
      }));
  }, [templates]);

  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    categorizedTemplates[0].elements[0].id,
  );

  const onClose = () => {
    track('CLOSE_EDITOR_TEMPLATES');
    setTemplatesPath(null);
  };

  const insertContent = () => {
    onClose();
    insertTemplate(editor, context, templatesPath, selectedTemplate, templates);
    const trackedTemplate = templates.find(
      (template) => template.id === selectedTemplate,
    );
    track('ADD_EDITOR_TEMPLATE', {
      templateId: selectedTemplate,
      templateVersion: trackedTemplate ? trackedTemplate.version : '',
    });
  };

  const onClick = (selectedOptionId: string) => {
    setSelectedTemplate(selectedOptionId);
  };

  return (
    <Drawer
      open={!!templatesPath}
      onClose={() => {
        onClose();
      }}
    >
      <TemplatesDrawerContainer>
        <TemplatesListContainer>
          <TemplatesListContainerInner>
            <TemplatesTitle>Add content from a template</TemplatesTitle>
            {categorizedTemplates.map(({ category, elements }) => (
              <section
                aria-label={`Template category: ${category}`}
                key={category}
              >
                <CategoryTitle>{category}</CategoryTitle>
                {elements.map(({ id, name }) => (
                  <TemplateChoiceOption
                    key={id}
                    value={name}
                    optionKey={id}
                    onClick={onClick}
                    isPressed={id === selectedTemplate}
                  />
                ))}
              </section>
            ))}
          </TemplatesListContainerInner>
        </TemplatesListContainer>
        <TemplatePreview
          templates={templates}
          selectedTemplateId={selectedTemplate}
        />
      </TemplatesDrawerContainer>
      <ActionButtonsContainer>
        <Button
          type="secondary"
          aria-label="Cancel"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          aria-label="Add to post"
          onClick={() => {
            insertContent();
          }}
        >
          Add to post
        </Button>
      </ActionButtonsContainer>
    </Drawer>
  );
};
